import React, { useEffect, useState, useMemo, useContext } from 'react';
import { toast } from "sonner";
import {
    Card,
    Button,
    Form,
    Input,
    InputNumber,
    Select,
    Row,
    Col,
    Typography,
    Space,
    Divider
} from 'antd';
import {
    getNativeClientPlatformPlan,
    updateNativeClientPlatformPlan,
    createNativeClientPlatformPlan
} from "../../../services/native-client-platform-plan";
import { getCompanies } from "../../../services/companies";
import { InputSearch } from "../../../components/input-search";
import Loading from "@/components/loading";
import UserContext from "../../../context/users";
import { AdminRole } from "../../../constants";
import { useNavigate, useParams } from 'react-router-dom';

const { Title, Text } = Typography;

export default function NativeClientPlatformPlansEdit() {
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = !!id;
    const [user] = useContext(UserContext);
    const isAdmin = user?.role === AdminRole;

    useEffect(() => {
        if (isEditing) {
            loadPlan(id);
        } else if (!isAdmin && user?.company?.id) {
            form.setFieldsValue({ companyID: user.company.id });
        }
    }, [id]);

    const loadPlan = (planId) => {
        setFetching(true);
        getNativeClientPlatformPlan(planId)
            .then((response) => {
                const planData = response.data || response;
                setInitialData(planData);

                const values = { ...planData };
                if (values.company) {
                    values.companyID = values.company.id;
                }
                form.setFieldsValue(values);
            })
            .catch((error) => {
                console.error(error);
                toast.error("Falha ao carregar detalhes do plano");
                navigate('/plans/native-client-platform');
            })
            .finally(() => setFetching(false));
    };

    const companyOptions = useMemo(() => {
        if (initialData?.company) {
            return [{ label: initialData.company.name, value: initialData.company.id }];
        }
        return undefined;
    }, [initialData]);

    const handleFinish = (values) => {
        setLoading(true);

        if (!isAdmin && user?.company?.id) {
            values.companyID = user.company.id;
        }

        const action = isEditing
            ? updateNativeClientPlatformPlan(id, values)
            : createNativeClientPlatformPlan(values);

        action
            .then(() => {
                toast.success(isEditing ? "Plano atualizado com sucesso!" : "Plano criado com sucesso!");
                if (!isEditing) navigate('../');
            })
            .catch((error) => {
                console.error(error);
                toast.error("Falha ao salvar plano");
            })
            .finally(() => setLoading(false));
    };

    if (fetching && !initialData) return <Loading title="Carregando plano..." />;

    return (
        <div>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>

                <Space direction="vertical" size={0}>
                    <Title level={2} style={{ margin: 0 }}>
                        {isEditing ? (initialData?.name || 'Editar Plano') : 'Novo Plano'}
                    </Title>
                    <Text type="secondary">
                        {isEditing
                            ? 'Gerencie as informações do plano da plataforma.'
                            : 'Preencha as informações para criar um novo plano.'}
                    </Text>
                </Space>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFinish}
                    variant="filled"
                >
                    <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                        <Col xs={24} md={16}>
                            <Card bordered={false}>
                                <Title level={5} style={{ marginBottom: 24, marginTop: 0 }}>Informações Gerais</Title>

                                <Row gutter={24}>
                                    {isAdmin ? (
                                        <>
                                            <Col xs={24} md={12}>
                                                <Form.Item
                                                    name="name"
                                                    label="Nome do Plano"
                                                    rules={[{ required: true, message: 'Por favor insira o nome do plano' }, { min: 2, max: 64 }]}
                                                >
                                                    <Input placeholder="Ex: Plano Premium" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <Form.Item
                                                    name="companyID"
                                                    label="Empresa"
                                                >
                                                    <InputSearch
                                                        getCallback={getCompanies}
                                                        {...(companyOptions ? { options: companyOptions } : {})}
                                                        placeholder="Selecione a empresa"
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </>
                                    ) : (
                                        <Col span={24}>
                                            <Form.Item
                                                name="name"
                                                label="Nome do Plano"
                                                rules={[{ required: true, message: 'Por favor insira o nome do plano' }, { min: 2, max: 64 }]}
                                            >
                                                <Input placeholder="Ex: Plano Premium" />
                                            </Form.Item>
                                        </Col>
                                    )}
                                </Row>

                                <Divider />

                                <Title level={5} style={{ marginBottom: 24, marginTop: 0 }}>Limites Técnicos</Title>
                                <Row gutter={24}>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            name="maxMessages"
                                            label="Máx. Mensagens"
                                            tooltip="Deixe vazio para ilimitado"
                                        >
                                            <InputNumber style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            name="maxUsers"
                                            label="Máx. Usuários"
                                            tooltip="Deixe vazio para ilimitado"
                                        >
                                            <InputNumber style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            name="maxSessions"
                                            label="Máx. Sessões"
                                            tooltip="Deixe vazio para ilimitado"
                                        >
                                            <InputNumber style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Divider />

                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                    <Button onClick={() => navigate('../')}>Cancelar</Button>
                                    <Button loading={loading} type="primary" htmlType="submit" size="large">
                                        {isEditing ? 'Salvar Plano' : 'Criar Plano'}
                                    </Button>
                                </div>
                            </Card>
                        </Col>

                        <Col xs={24} md={8}>
                            <Card bordered={false}>
                                <Title level={5} style={{ marginBottom: 24, marginTop: 0 }}>Precificação</Title>
                                <Form.Item
                                    name="price"
                                    label="Preço Mensal"
                                    rules={[{ required: true, message: 'Obrigatório' }]}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        formatter={value => `R$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        parser={value => value.replace(/R\$\s?|(,*)/g, '')}
                                    />
                                </Form.Item>

                                <Divider style={{ margin: '12px 0' }} />

                                <Title level={5} style={{ marginBottom: 24, marginTop: 0 }}>Funcionalidades</Title>
                                <Form.Item
                                    name="features"
                                    label="Features"
                                    tooltip="Digite e pressione Enter para adicionar"
                                >
                                    <Select
                                        mode="tags"
                                        style={{ width: '100%' }}
                                        placeholder="Ex: Suporte prioritário"
                                        tokenSeparators={[',']}
                                    />
                                </Form.Item>
                            </Card>
                        </Col>
                    </Row>
                </Form>
            </Space>
        </div>
    );
}
